import React, { useMemo, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";
import PropTypes from "prop-types";

const AsyncSearchSelect = ({
  value,
  onChange, // (selectedOption) => void
  loadOptions, // async function (inputValue) => Promise<Option[]>
  placeholder = "Search...",
  minInputLength = 2,
  debounceWait = 500,
  cacheOptions = true,
  defaultOptions = [], // show cached/default options before typing
  isClearable = true,
  isDisabled = false,
  isLoading = false,
  noOptionsMessage: customNoOptionsMessage,
  formatOptionLabel,
  getOptionLabel = (opt) => opt.label,
  getOptionValue = (opt) => opt.value,
  styles: customStyles = {},
  className = "",
  classNamePrefix = "react-select",
  menuPortalTarget,
  menuPosition = "fixed",
  ...rest
}) => {
  // Debounce the loadOptions call
  const debouncedLoadOptions = useMemo(
    () =>
      debounce((inputValue, callback) => {
        if (inputValue?.trim().length < minInputLength) {
          // Return cached/default options or empty
          if (defaultOptions && typeof defaultOptions === "function") {
            defaultOptions(inputValue).then(callback);
          } else {
            callback(defaultOptions || []);
          }
          return;
        }

        loadOptions(inputValue)
          .then((options) => callback(options))
          .catch((err) => {
            console.error("Async load error:", err);
            callback([]);
          });
      }, debounceWait),
    [loadOptions, debounceWait, minInputLength, defaultOptions],
  );

  // Memoized no options message
  const noOptionsMsg = useCallback(
    ({ inputValue }) => {
      if (customNoOptionsMessage) {
        return customNoOptionsMessage({ inputValue });
      }
      if (inputValue?.trim().length < minInputLength) {
        return `Type at least ${minInputLength} characters to search`;
      }
      return "No results found";
    },
    [customNoOptionsMessage, minInputLength],
  );

  // Default react-select styles (can be overridden)
  const defaultSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      borderColor: state.isFocused ? "#80bdff" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
      "&:hover": {
        borderColor: "#80bdff",
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#007bff"
        : state.isFocused
          ? "#e9ecef"
          : null,
      color: state.isSelected ? "white" : null,
    }),
  };

  return (
    <AsyncSelect
      cacheOptions={cacheOptions || []}
      defaultOptions={defaultOptions}
      loadOptions={debouncedLoadOptions}
      debounceInterval={debounceWait}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      noOptionsMessage={noOptionsMsg}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      styles={{ ...defaultSelectStyles, ...customStyles }}
      className={`async-search-select ${className}`}
      classNamePrefix={classNamePrefix}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
      {...rest}
    />
  );
};

AsyncSearchSelect.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  loadOptions: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  minInputLength: PropTypes.number,
  debounceWait: PropTypes.number,
  cacheOptions: PropTypes.bool,
  defaultOptions: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.array,
    PropTypes.func,
  ]),
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  noOptionsMessage: PropTypes.func,
  formatOptionLabel: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  styles: PropTypes.object,
  className: PropTypes.string,
  classNamePrefix: PropTypes.string,
  menuPortalTarget: PropTypes.any,
  menuPosition: PropTypes.oneOf(["absolute", "fixed"]),
};

export default AsyncSearchSelect;
